import React, { useEffect, useState, useRef } from "react";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Upload, Edit2, Table2, X, Hash, Key, Download } from "lucide-react";

const LOOKUP_TYPES = [
  { value: "key_value", label: "Key → Value", desc: "Match exact key, return value column" },
  { value: "range", label: "Numeric Range", desc: "Check if number falls between lower/upper, return value" },
];

function KeyValueEditor({ columns, setColumns, rows, setRows }) {
  const addColumn = () => {
    const newCol = `col${columns.length + 1}`;
    setColumns([...columns, newCol]);
    setRows(rows.map(r => ({ ...r, [newCol]: "" })));
  };

  const renameColumn = (ci, newName) => {
    const newCols = [...columns];
    const oldCol = newCols[ci];
    newCols[ci] = newName;
    setColumns(newCols);
    setRows(rows.map(r => {
      const nr = { ...r, [newName]: r[oldCol] ?? "" };
      delete nr[oldCol];
      return nr;
    }));
  };

  const removeColumn = (ci) => {
    const col = columns[ci];
    setColumns(columns.filter((_, i) => i !== ci));
    setRows(rows.map(r => { const nr = { ...r }; delete nr[col]; return nr; }));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-gray-500">Columns</Label>
        <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={addColumn}>
          <Plus size={10} className="mr-1" /> Add Column
        </Button>
      </div>
      <div className="flex gap-1.5 flex-wrap">
        {columns.map((col, ci) => (
          <div key={ci} className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded px-2 py-1">
            {ci === 0 && <Key size={10} className="text-[#006080] mr-0.5" />}
            <input
              className="text-xs bg-transparent border-0 outline-0 w-24"
              value={col}
              onChange={e => renameColumn(ci, e.target.value)}
            />
            {columns.length > 1 && (
              <button onClick={() => removeColumn(ci)}>
                <X size={10} className="text-gray-400 hover:text-red-500" />
              </button>
            )}
          </div>
        ))}
      </div>
      <div className="text-xs text-gray-400">First column is the lookup key (marked with <Key size={10} className="inline" />)</div>
      <DataGrid columns={columns} rows={rows} setRows={setRows} />
    </div>
  );
}

function RangeEditor({ columns, setColumns, rows, setRows }) {
  const DEFAULT_COLS = ["lower", "upper", "value"];

  // Use effect to initialize if columns are wrong for range type
  useEffect(() => {
    if (columns.length < 3 || (!columns.includes("lower") && !columns.includes("upper"))) {
      setColumns(DEFAULT_COLS);
      setRows(rows.map(() => ({ lower: "", upper: "", value: "" })));
    }
  }, []); // eslint-disable-line

  const safeCols = columns.length >= 3 ? columns : DEFAULT_COLS;

  const addValueCol = () => {
    const newCol = `value${safeCols.filter(c => c !== "lower" && c !== "upper").length + 1}`;
    const newCols = [...safeCols, newCol];
    setColumns(newCols);
    setRows(rows.map(r => ({ ...r, [newCol]: "" })));
  };

  const renameCol = (ci, newName) => {
    if (ci < 2) return;
    const newCols = [...safeCols];
    const old = newCols[ci];
    newCols[ci] = newName;
    setColumns(newCols);
    setRows(rows.map(r => { const nr = { ...r, [newName]: r[old] ?? "" }; delete nr[old]; return nr; }));
  };

  return (
    <div className="space-y-3">
      <div className="bg-[#e6f2f5] border border-[#006080]/20 rounded-md p-3 text-xs text-[#006080]">
        <strong>Range Lookup:</strong> Checks if the source column value falls between <code>lower ≤ value ≤ upper</code> and returns the matched value column.
      </div>
      <div className="grid grid-cols-3 gap-2 items-end">
        <div>
          <Label className="text-xs text-gray-500">Lower Bound Col</Label>
          <div className="h-8 mt-1 px-3 bg-gray-50 border border-gray-200 rounded-md flex items-center text-xs font-mono text-gray-600">lower</div>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Upper Bound Col</Label>
          <div className="h-8 mt-1 px-3 bg-gray-50 border border-gray-200 rounded-md flex items-center text-xs font-mono text-gray-600">upper</div>
        </div>
        <Button size="sm" variant="outline" className="h-8 px-2 text-xs" onClick={addValueCol}>
          <Plus size={10} className="mr-1" /> Add Value Col
        </Button>
      </div>
      {safeCols.slice(2).map((col, ci) => (
        <div key={ci} className="flex items-center gap-2">
          <Label className="text-xs text-gray-500 w-28 flex-shrink-0">Value Column {ci + 1}</Label>
          <input
            className="h-7 px-2 text-xs border border-gray-200 rounded-md focus:border-[#006080] focus:outline-none flex-1"
            value={col}
            onChange={e => renameCol(ci + 2, e.target.value)}
          />
        </div>
      ))}
      <DataGrid columns={safeCols} rows={rows} setRows={setRows} />
    </div>
  );
}

function DataGrid({ columns, rows, setRows }) {
  const addRow = () => {
    const emptyRow = {};
    columns.forEach(c => emptyRow[c] = "");
    setRows([...rows, emptyRow]);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs text-gray-500">Data ({rows.length} rows)</Label>
        <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={addRow}>
          <Plus size={10} className="mr-1" /> Add Row
        </Button>
      </div>
      <div className="border border-gray-200 rounded-md overflow-auto max-h-48">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              {columns.map(c => <th key={c} className="px-2 py-1.5 text-left text-gray-500 font-semibold whitespace-nowrap">{c}</th>)}
              <th className="px-2 py-1.5 w-6"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} className="border-t border-gray-100">
                {columns.map(col => (
                  <td key={col} className="px-1 py-0.5">
                    <input
                      className="w-full min-w-[60px] px-1 py-0.5 border border-transparent rounded focus:border-[#006080] focus:outline-none text-xs"
                      value={row[col] ?? ""}
                      onChange={e => {
                        const newRows = [...rows];
                        newRows[ri] = { ...row, [col]: e.target.value };
                        setRows(newRows);
                      }}
                    />
                  </td>
                ))}
                <td className="px-1 py-0.5">
                  <button onClick={() => setRows(rows.filter((_, i) => i !== ri))}>
                    <X size={11} className="text-gray-300 hover:text-red-500" />
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={columns.length + 1} className="px-3 py-4 text-center text-gray-300 text-xs">No data rows. Click Add Row.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LookupEditor({ lookup, onSave, onClose }) {
  const [name, setName] = useState(lookup?.name || "");
  const [description, setDescription] = useState(lookup?.description || "");
  const [lookupType, setLookupType] = useState(lookup?.lookup_type || "key_value");
  const [columns, setColumns] = useState(
    lookup?.columns || (lookupType === "range" ? ["lower", "upper", "value"] : ["key", "value"])
  );
  const [rows, setRows] = useState(lookup?.rows || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const switchType = (newType) => {
    setLookupType(newType);
    if (newType === "range") {
      setColumns(["lower", "upper", "value"]);
      setRows(rows.map(() => ({ lower: "", upper: "", value: "" })));
    } else {
      setColumns(["key", "value"]);
      setRows(rows.map(() => ({ key: "", value: "" })));
    }
  };

  const handleSave = async () => {
    if (!name) { setError("Name is required."); return; }
    if (columns.length === 0) { setError("Add at least one column."); return; }
    setSaving(true);
    try {
      await onSave({ name, description, lookup_type: lookupType, columns, rows });
      onClose();
    } catch (err) {
      setError(formatApiError(err));
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base font-bold">{lookup ? "Edit" : "Create"} Lookup Table</DialogTitle>
        </DialogHeader>
        {error && <div className="p-2 bg-red-50 rounded text-red-700 text-sm mb-2">{error}</div>}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Name *</Label>
              <Input className="mt-1" value={name} onChange={e => setName(e.target.value)} placeholder="Product Codes" data-testid="lookup-name-input" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Description</Label>
              <Input className="mt-1" value={description} onChange={e => setDescription(e.target.value)} />
            </div>
          </div>

          {/* Lookup type */}
          <div>
            <Label className="text-xs text-gray-500 mb-2 block">Lookup Type</Label>
            <div className="grid grid-cols-2 gap-2">
              {LOOKUP_TYPES.map(lt => (
                <button
                  key={lt.value}
                  className={`p-3 rounded-lg border text-left transition-all ${lookupType === lt.value ? "border-[#006080] bg-[#e6f2f5]" : "border-gray-200 bg-white hover:border-gray-300"}`}
                  onClick={() => switchType(lt.value)}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {lt.value === "key_value" ? <Key size={14} className="text-[#006080]" /> : <Hash size={14} className="text-[#006080]" />}
                    <span className="text-sm font-semibold text-gray-800">{lt.label}</span>
                  </div>
                  <p className="text-xs text-gray-400">{lt.desc}</p>
                </button>
              ))}
            </div>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/lookups/template/${lookupType}`}
              download
              className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#006080] hover:underline"
            >
              <Download size={12} /> Download CSV template for bulk upload
            </a>
          </div>

          {/* Data editor */}
          {lookupType === "key_value" ? (
            <KeyValueEditor columns={columns} setColumns={setColumns} rows={rows} setRows={setRows} />
          ) : (
            <RangeEditor columns={columns} setColumns={setColumns} rows={rows} setRows={setRows} />
          )}
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={handleSave} disabled={saving} data-testid="save-lookup-btn">
            {saving ? "Saving..." : "Save Lookup Table"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LookupTablesPage() {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => { loadTables(); }, []);

  const loadTables = async () => {
    setLoading(true);
    const res = await client.get("/lookups/").catch(() => ({ data: [] }));
    setTables(res.data);
    setLoading(false);
  };

  const saveTable = async (form) => {
    if (editingTable) {
      await client.put(`/lookups/${editingTable._id}`, form);
    } else {
      await client.post("/lookups/", form);
    }
    await loadTables();
  };

  const deleteTable = async (id) => {
    if (!window.confirm("Delete this lookup table?")) return;
    await client.delete(`/lookups/${id}`);
    loadTables();
  };

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} lookup table(s)?`)) return;
    setBulkDeleting(true);
    try {
      await client.post("/lookups/bulk-delete", { ids: [...selected] });
      setSelected(new Set());
      await loadTables();
    } finally { setBulkDeleting(false); }
  };

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const uploadFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const name = prompt("Lookup table name:", file.name.split(".")[0]);
    if (!name) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", name);
    fd.append("description", "");
    await client.post("/lookups/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    await loadTables();
    e.target.value = "";
  };

  return (
    <div className="p-6 max-w-5xl mx-auto fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Lookup Tables</h1>
          <p className="text-gray-500 text-sm mt-0.5">Named reference tables for data mapping and enrichment</p>
        </div>
        <div className="flex gap-2">
          {selected.size > 0 && (
            <Button variant="outline" className="text-red-500 hover:bg-red-50 hover:border-red-200" onClick={bulkDelete} disabled={bulkDeleting}>
              <Trash2 size={14} className="mr-1.5" />{bulkDeleting ? "Deleting..." : `Delete ${selected.size}`}
            </Button>
          )}
          <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={uploadFile} />
          <Button variant="outline" className="border-gray-200" onClick={() => fileInputRef.current?.click()}>
            <Upload size={15} className="mr-2" /> Upload File
          </Button>
          <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={() => { setEditingTable(null); setShowEditor(true); }} data-testid="create-lookup-btn">
            <Plus size={15} className="mr-2" /> New Lookup Table
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-white border border-gray-200 rounded-lg animate-pulse" />)}</div>
      ) : tables.length === 0 ? (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
          <Table2 size={40} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-400">No lookup tables yet</p>
          <Button className="mt-4 bg-[#006080] hover:bg-[#004d66] text-white" onClick={() => setShowEditor(true)}>
            Create your first lookup table
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {tables.map(t => (
            <div key={t._id} className={`bg-white border rounded-lg p-4 hover:shadow-sm transition-all ${selected.has(t._id) ? "border-[#006080] bg-[#F0F8FA]" : "border-gray-200"}`} data-testid={"lookup-table-" + t._id}>
              <div className="flex items-center gap-3">
                <input type="checkbox" className="w-4 h-4 rounded flex-shrink-0" checked={selected.has(t._id)} onChange={() => toggleSelect(t._id)} />
                <div className="w-9 h-9 bg-[#e6f2f5] rounded-md flex items-center justify-center flex-shrink-0">
                  {t.lookup_type === "range" ? <Hash size={18} className="text-[#006080]" /> : <Table2 size={18} className="text-[#006080]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-gray-900">{t.name}</p>
                    <Badge variant="secondary" className="text-xs capitalize">{t.lookup_type === "range" ? "Numeric Range" : "Key → Value"}</Badge>
                  </div>
                  {t.description && <p className="text-xs text-gray-400 mt-0.5">{t.description}</p>}
                  <div className="flex gap-2 mt-1.5">
                    <Badge variant="secondary" className="text-xs">{t.row_count} rows</Badge>
                    <Badge variant="secondary" className="text-xs font-mono">{t.columns?.join(", ")}</Badge>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => { setEditingTable(t); setShowEditor(true); }}>
                    <Edit2 size={12} className="mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-500 hover:bg-red-50 hover:border-red-200" onClick={() => deleteTable(t._id)}>
                    <Trash2 size={12} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showEditor && (
        <LookupEditor
          lookup={editingTable}
          onSave={saveTable}
          onClose={() => { setShowEditor(false); setEditingTable(null); }}
        />
      )}
    </div>
  );
}
